import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Typography,
  Container,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Autocomplete,
  CircularProgress,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel,
  Alert,
  Button,
} from '@mui/material';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  useNotification,
  notifyPermissionError,
} from '@1823-partners/core';
import {
  ClientSideRowModelModule,
  ValidationModule,
  PinnedRowModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  NumberEditorModule,
  RenderApiModule,
  CellStyleModule,
} from "ag-grid-community";

import ProductManager from "./ProductManager";
import MetricsPanel from "./MetricsPanel";
import AlmChart from "./AlmChart";
import AssetsBvChart from "./AssetsBvChart";
import api, {
  fetchAllWeights,
  fetchAssetClassData,
  fetchMetrics,
  fetchAlmGraphData,
  fetchAssetsBvData,
} from "../api";

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export default function Main({ availableDates, asof, setAsof }) {
  // Constants
  const EPSILON = 1e-9;

  // App-wide notifications (used to surface permission-denied guidance).
  const { showNotification } = useNotification();

  // Grid ref for forcing updates
  const gridRef = useRef(null);

  // Per-user view-options (saved column/sort/filter layouts) for the assets grid.
  const gridConfigs = useGridConfigs(api, {
    app: "alm",
    gridId: "assets",
    gridApiRef: gridRef,
  });

  // Dashboard States
  const [assetData, setAssetData] = useState([]); // Array of assets
  const [staticWeights, setStaticWeights] = useState({});
  const [metricsData, setMetricsData] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [securitySelection, setSecuritySelection] = useState(50); // Default to 50
  const [securitySelectionInput, setSecuritySelectionInput] = useState(50); // For the input field
  const [day1Expense, setDay1Expense] = useState(0); // Default to 0
  const [day1ExpenseInput, setDay1ExpenseInput] = useState(0); // For the input field
  const [reinvestmentYield, setReinvestmentYield] = useState(0); // Default to 0 (0%)
  const [reinvestmentYieldInput, setReinvestmentYieldInput] = useState(0); // For the input field as percentage number

  // Dynamic columns and graph selection
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [allAlmData, setAllAlmData] = useState({}); // Store all ALM data
  const [allAssetsBvData, setAllAssetsBvData] = useState({}); // Store all Assets vs BV data
  const [almProductSelection, setAlmProductSelection] = useState(""); // Single product for ALM
  const [assetsBvProductSelections, setAssetsBvProductSelections] = useState([]); // Multiple products for Assets vs Liabilities
  const [loadingGraph, setLoadingGraph] = useState(false);
  // New state for CUSIP filter using Autocomplete (multiple selection)
  const [cusipFilter, setCusipFilter] = useState([]);

  // Shock selection states
  const [metricsShockSelection, setMetricsShockSelection] = useState(['No Shock']); // Multi-select array
  const [almShockSelection, setAlmShockSelection] = useState('No Shock'); // Single select for ALM
  const [assetsBvShockSelections, setAssetsBvShockSelections] = useState(['No Shock']); // Multi-select for Assets vs Liabilities
  
  // Scale switch state
  const [scaleEnabled, setScaleEnabled] = useState(false);

  // New state for manual loading system
  const [changesDetected, setChangesDetected] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // New state for editable functionality
  const [originalAssetData, setOriginalAssetData] = useState([]); // Store original values
  const [updatedSpreads, setUpdatedSpreads] = useState(null); // Track spread edits
  const [updatedYields, setUpdatedYields] = useState(null); // Track yield edits
  const [updatedWeights, setUpdatedWeights] = useState({}); // Track weight edits by field

  // Check if Up 100 shock should be enabled
  const currentRegions = [
    ...(almProductSelection ? [almProductSelection.split('_')[1]] : []),
    ...assetsBvProductSelections.map(selection => selection.split('_')[1])
  ];
  const isShockEnabled = currentRegions.some(region => {
    if (region.includes('SPDA')) {
      return asof === '2025-05-28';
    }
    if (region === 'FIA NY') {
      return new Date(asof) >= new Date('2025-07-16');
    }
    return false;
  });

  // Computed values for filtered data based on dropdown selections
  const almData = useMemo(() => {
    if (!almProductSelection || !allAlmData || Object.keys(allAlmData).length === 0) return {};
    
    // Convert product key format: "3Y_SPDA NY" -> "SPDA NY 3Y"
    const [tenor, region] = almProductSelection.split("_");
    const apiProductKey = `${region} ${tenor}`;
    
    return allAlmData[apiProductKey] || {};
  }, [allAlmData, almProductSelection]);

  const assetsBvData = useMemo(() => {
    if (!assetsBvProductSelections.length || !allAssetsBvData || Object.keys(allAssetsBvData).length === 0) return [];
    
    return assetsBvProductSelections.map(selection => {
      // Convert product key format: "3Y_SPDA NY" -> "SPDA NY 3Y"
      const [tenor, region] = selection.split("_");
      const apiProductKey = `${region} ${tenor}`;
      
      return {
        selection,
        name: selection.replace('_', ' '),
        data: allAssetsBvData[apiProductKey] || {}
      };
    });
  }, [allAssetsBvData, assetsBvProductSelections]);

  // Debounced security selection update
  useEffect(() => {
    const timer = setTimeout(() => {
      setSecuritySelection(securitySelectionInput);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [securitySelectionInput]);

  // Debounced day1Expense update
  useEffect(() => {
    const timer = setTimeout(() => {
      setDay1Expense(day1ExpenseInput);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [day1ExpenseInput]);

  // Debounced reinvestment yield update with percentage conversion
  useEffect(() => {
    const timer = setTimeout(() => {
      // Convert percentage to decimal: 5.5% -> 0.055
      setReinvestmentYield(reinvestmentYieldInput / 100);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [reinvestmentYieldInput]);

  // Reset invalid shock selections when conditions change
  useEffect(() => {
    // Helper function to filter valid shocks
    const filterValidShocks = (shocks, allowMultiple = false) => {
      const validShocks = (Array.isArray(shocks) ? shocks : [shocks]).filter(shock => {
        if (shock === 'No Shock') return true;
        if (shock === 'Up 100 shock') return isShockEnabled;
        if (shock === 'Down 100 shock') return isShockEnabled;
        return true;
      });
      
      if (allowMultiple) {
        return validShocks.length > 0 ? validShocks : ['No Shock'];
      } else {
        return validShocks.length > 0 ? validShocks[0] : 'No Shock';
      }
    };

    // Reset metrics shock selection
    const validMetricsShocks = filterValidShocks(metricsShockSelection, true);
    if (JSON.stringify(validMetricsShocks) !== JSON.stringify(metricsShockSelection)) {
      setMetricsShockSelection(validMetricsShocks);
    }

    // Reset ALM shock selection
    const validAlmShock = filterValidShocks(almShockSelection, false);
    if (validAlmShock !== almShockSelection) {
      setAlmShockSelection(validAlmShock);
    }

    // Reset Assets vs Liabilities shock selections
    const validAssetsBvShocks = filterValidShocks(assetsBvShockSelections, true);
    if (JSON.stringify(validAssetsBvShocks) !== JSON.stringify(assetsBvShockSelections)) {
      setAssetsBvShockSelections(validAssetsBvShocks);
    }
  }, [almProductSelection, assetsBvProductSelections, asof, isShockEnabled, metricsShockSelection, almShockSelection, assetsBvShockSelections]);

  // Extract unique CUSIP options from assetData
  const cusipOptions = useMemo(() => {
    const options = assetData.map((asset) => asset.cusip);
    return [...new Set(options)];
  }, [assetData]);

  const formatNumerical = (params) => {
    const value = params.value;
    return value !== undefined && value !== null ? value.toFixed(2) : "";
  };



  // Add CSS for right alignment that actually works
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ag-cell.ag-right-aligned {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-end !important;
        text-align: right !important;
      }
      .ag-cell.ag-right-aligned .ag-cell-wrapper {
        width: 100% !important;
        text-align: right !important;
      }
      .ag-cell.edited-cell {
        background-color: #fff3cd !important;
        border-left: 4px solid #ffc107 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Helper function to handle reset
  const handleResetField = (field) => {
    console.log('Reset called for field:', field);
    console.log('Original asset data length:', originalAssetData.length);
    console.log('Current updatedSpreads:', updatedSpreads);
    console.log('Current updatedYields:', updatedYields);
    
    if (field === 'zspread') {
      console.log('Resetting ZSpread');
      setUpdatedSpreads(null);
      
      // Reset asset data to original values
      setAssetData(prev => {
        const newData = prev.map(asset => {
          const originalAsset = originalAssetData.find(orig => orig.cusip === asset.cusip);
          if (originalAsset) {
            console.log(`Resetting ${asset.cusip} zspread from ${asset.zspread} to ${originalAsset.zspread}`);
            return {
              ...asset,
              zspread: originalAsset.zspread
            };
          }
          return asset;
        });
        console.log('New asset data after reset:', newData.slice(0, 3)); // Log first 3 items
        
        return newData;
      });
    } else if (field === 'mkt_yld') {
      console.log('Resetting Market Yield');
      setUpdatedYields(null);
      
      // Reset asset data to original values
      setAssetData(prev => {
        const newData = prev.map(asset => {
          const originalAsset = originalAssetData.find(orig => orig.cusip === asset.cusip);
          if (originalAsset) {
            console.log(`Resetting ${asset.cusip} mkt_yld from ${asset.mkt_yld} to ${originalAsset.mkt_yld}`);
            return {
              ...asset,
              mkt_yld: originalAsset.mkt_yld
            };
          }
          return asset;
        });
        console.log('New asset data after reset:', newData.slice(0, 3)); // Log first 3 items
        
        return newData;
      });
    } else {
      // Reset dynamic weight field
      console.log('Resetting weight field:', field);
      
      // Clear the weight edits for this field
      const weights = { ...updatedWeights };
      delete weights[field];
      setUpdatedWeights(Object.keys(weights).length > 0 ? weights : {});
      
      // Reset asset data to original values
      setAssetData(prev => {
        const newData = prev.map(asset => {
          const staticWeight = staticWeights[field]?.[asset.name || asset.cusip] || 0;
          console.log(`Resetting ${asset.cusip} ${field} from ${asset.weights?.[field] || 0} to ${staticWeight}`);
          return {
            ...asset,
            weights: {
              ...asset.weights,
              [field]: staticWeight
            }
          };
        });
        console.log('New asset data after reset:', newData.slice(0, 3)); // Log first 3 items
        
        return newData;
      });
    }
    
    clearDataAndSetChanges();
  };

// helper that returns a rules object for any field
const makeClassRules = (field) => ({
  'ag-right-aligned': () => true,
  'edited-cell': (params) => {
    const cusip = params.data.cusip;
    // find the original asset row
    const orig = originalAssetData.find(a => a.cusip === cusip);
    if (!orig) return false;

    // decide original vs current value
    let origValue, curValue = params.value || 0;
    if (baseFields.includes(field)) {
      // raw number
      origValue = orig[field] ?? 0;
    } else {
      // dynamic weight: orig.weights[field] is fraction => convert to %
      origValue = (orig.weights?.[field] || 0) * 100;
    }

    return Math.abs(curValue - origValue) > 0.001;
  }
});

  // List of CUSIPs that cannot have their yield edited
  const nonEditableYieldCusips = [
    'CLO BBB BB Combo Note',
    'Long Dur PE Alts',
    'Non QM Vista Point Loan',
    'Non QM Vista Pt Loan',
    'Real Asset - EJ structure',
    'Real Asset - EJ structure LONG',
    'Real Asset - Kroll structure',
    'Residential Mortgage Loans',
    'Sample_Asset',
    'Short Dur PE Alts',
    'USD Private Equity'
  ];

  // **AG Grid: Column Definitions**
  const baseColumns = [
    { headerName: "CUSIP", field: "cusip", sortable: true, filter: "agTextColumnFilter", editable: false },
    { 
      headerName: 'ZSpread',
      field: 'zspread',
      editable: true,
      cellClassRules: makeClassRules('zspread'),
      cellEditor: 'agNumberCellEditor',
      filter: "agNumberColumnFilter", 
      valueFormatter: formatNumerical,
    },
    { 
      headerName: "Market Price", 
      field: "mkt_px", 
      sortable: true, 
      filter: "agNumberColumnFilter", 
      editable: false, 
      valueFormatter: formatNumerical,
      cellClass: 'ag-right-aligned'
    },
    { 
      headerName: "Eff. Duration", 
      field: "eff_dur", 
      sortable: true, 
      filter: "agNumberColumnFilter", 
      editable: false, 
      valueFormatter: formatNumerical,
      cellClass: 'ag-right-aligned'
    },
    { 
      headerName: "Convexity", 
      field: "cvx", 
      sortable: true, 
      filter: "agNumberColumnFilter", 
      editable: false, 
      valueFormatter: formatNumerical,
      cellClass: 'ag-right-aligned'
    },
    { 
      headerName: 'Market Yield',
      field: 'mkt_yld',
      editable: (params) => !nonEditableYieldCusips.includes(params.data.cusip),
      cellClassRules: makeClassRules('mkt_yld'),
      cellEditor: 'agNumberCellEditor',
      filter: "agNumberColumnFilter", 
      valueFormatter: formatNumerical,
    },
    {
      headerName: "Market Date", field: "mkt_data_date", sortable: true,
      filter: "agDateColumnFilter", editable: false,
      // mkt_data_date arrives as a midnight date serialized 'GMT' (it is not a
      // tz-naive datetime column, so the central NTZ->UTC serializer skips it).
      // Parse in the UTC frame to preserve the wall-clock day; local dayjs()
      // shifts it back a day in any US-Eastern browser.
      valueFormatter: (params) => (params.value ? dayjs.utc(params.value).format("M/D/YYYY") : ""),
    },
    { 
      headerName: "WAL", 
      field: "wal", 
      sortable: true, 
      filter: "agNumberColumnFilter", 
      editable: false, 
      valueFormatter: formatNumerical,
      cellClass: 'ag-right-aligned'
    },
  ];

  // Define base fields for distinguishing between base columns and dynamic columns
  const baseFields = [
    "cusip",
    "zspread",
    "mkt_px",
    "eff_dur",
    "cvx",
    "mkt_yld",
    "mkt_data_date",
    "wal",
  ];

  // Create columnDefs for any dynamic product columns
  const dynamicDefs = useMemo(
    () =>
      dynamicColumns.map((col) => ({
        headerName: col.headerName,
        field: col.field,
        editable: true,
        filter: "agNumberColumnFilter",
        cellClassRules: makeClassRules(col.field),
        cellEditor: 'agNumberCellEditor',
        valueFormatter: (params) => {
          const value = params.value;
          if (typeof value === "string") {
            return value;
          }
          return value !== undefined && value !== null ? `${value.toFixed(2)}%` : "";
        },
      })),
    [dynamicColumns, originalAssetData, staticWeights]
  );

  const defaultColDef = {
    flex: 1,
    minWidth: 100,
    resizable: true,
    sortable: true,
    filter: "agNumberColumnFilter",
    editable: true,
  };

  // Build row data for AG Grid
  const rowData = useMemo(() => {
    return (assetData || [])
      .filter((asset) => {
        if (cusipFilter.length === 0) return true;
        return cusipFilter.includes(asset.cusip);
      })
      .map((asset) => {
        let row = { ...asset };
        dynamicColumns.forEach((col) => {
          const weight = asset.weights?.[col.field] || 0; // fraction
          row[col.field] = weight * 100; // convert to percentage
        });
        row.id = asset.cusip;
        return row;
      });
  }, [assetData, dynamicColumns, cusipFilter]);

  // Pinned bottom row with sum of dynamic columns
  const pinnedBottomRowData = useMemo(() => {
    if (!dynamicColumns.length) return [];
    const sums = { cusip: "Sum" };
    dynamicColumns.forEach((col) => {
      const field = col.field;
      const sum = rowData.reduce((acc, row) => acc + (row[field] || 0), 0);
      const sumFormatted = sum.toFixed(2);
      let indicator = "";
      if (Math.abs(sum - 100) < 0.01) {
        indicator = " ✓";
      } else if (sum < 100) {
        indicator = " ↓";
      } else {
        indicator = " ↑";
      }
      sums[field] = `${sumFormatted}%${indicator}`;
    });
    return [sums];
  }, [rowData, dynamicColumns]);

  // Recalculation Function
  const recalcData = useCallback(
    async (newAssetData) => {
      const assets = newAssetData || assetData;
  
      // If no dynamic columns, just clear
      if (dynamicColumns.length === 0) {
        setMetricsData([]);
        setAllAlmData({});
        setAllAssetsBvData({});
        return;
      }
  
      if (!assets.length || !asof) return;
  
      // Build weights object for the API
      const weights = {};
      dynamicColumns.forEach((col) => {
        const key = col.field;
        const nonZeroWeights = {};
        assets.forEach((asset) => {
          if (asset.weights && asset.weights[key] > 0) {
            const assetName = asset.name || asset.cusip || "unknown";
            nonZeroWeights[assetName] = asset.weights[key];
          }
        });
        if (Object.keys(nonZeroWeights).length > 0) {
          weights[key] = nonZeroWeights;
        }
      });
  
      try {
        // METRICS - always recalculate
        setLoadingMetrics(true);
        setLoadingGraph(true);
        const metricsResp = await fetchMetrics(asof, weights, securitySelection, day1Expense, null, null, reinvestmentYield);
        setMetricsData(metricsResp.data || []);
        setLoadingMetrics(false);
        // If there are currently selected products
        if (dynamicColumns.length > 0) {
          // Fetch all ALM data at once
          const almResp = await fetchAlmGraphData(asof, weights, securitySelection, day1Expense, null, null, reinvestmentYield);
          const almDataToStore = almResp?.data?.data || almResp?.data || {};
          setAllAlmData(almDataToStore);
          
          // Fetch all Assets vs BV data at once
          const assetsBvResp = await fetchAssetsBvData(asof, weights, securitySelection, day1Expense, null, null, reinvestmentYield);
          const assetsBvDataToStore = assetsBvResp?.data?.data || assetsBvResp?.data || {};
          setAllAssetsBvData(assetsBvDataToStore);
          
          setLoadingGraph(false);
        } else {
          // No products, clear charts
          setAllAlmData({});
          setAllAssetsBvData({});
        }
      } catch (error) {
        if (!notifyPermissionError(showNotification, error)) {
          console.error("Error recalculating data:", error);
        }
      }
    },
    [assetData, asof, dynamicColumns, almProductSelection, assetsBvProductSelections, securitySelection, day1Expense]
  );

  // Manual load function
  const handleLoadData = async () => {
    setIsLoadingData(true);
    setChangesDetected(false);
    
    // Ensure we have the required data
    if (!assetData.length || !asof || dynamicColumns.length === 0) {
      setIsLoadingData(false);
      return;
    }

    // Store original asset data if not already stored
    if (originalAssetData.length === 0) {
      setOriginalAssetData([...assetData]);
    }

    // Build weights object for the API
    const weights = {};
    dynamicColumns.forEach((col) => {
      const key = col.field;
      const nonZeroWeights = {};
      assetData.forEach((asset) => {
        if (asset.weights && asset.weights[key] > 0) {
          const assetName = asset.name || asset.cusip || "unknown";
          nonZeroWeights[assetName] = asset.weights[key];
        }
      });
      if (Object.keys(nonZeroWeights).length > 0) {
        weights[key] = nonZeroWeights;
      }
    });

    try {
      // METRICS - always recalculate
      setLoadingMetrics(true);
      setLoadingGraph(true);
      const metricsResp = await fetchMetrics(asof, weights, securitySelection, day1Expense, updatedSpreads, updatedYields, reinvestmentYield);
      setMetricsData(metricsResp.data || []);
      setLoadingMetrics(false);
      
      // If there are currently selected products
      if (dynamicColumns.length > 0) {
        const assetsBvDataResults = [];
        
        // Fetch all ALM data at once
        if (almProductSelection) {
          const almResp = await fetchAlmGraphData(asof, weights, securitySelection, day1Expense, updatedSpreads, updatedYields, reinvestmentYield);
          
          // Convert product key format: "3Y_SPDA NY" -> "SPDA NY 3Y"
          const [tenor, region] = almProductSelection.split("_");
          const apiProductKey = `${region} ${tenor}`;
          
          // Handle response format: { "data": { "SPDA NY 3Y": {}, "SPDA NY 5Y": {} } }
          const almDataForProduct = almResp?.data?.data?.[apiProductKey] || almResp?.data?.[apiProductKey] || {};
          setAllAlmData(almResp?.data?.data || almResp?.data || {});
        } else {
          setAllAlmData({});
        }
        
        // Fetch all Assets vs BV data at once
        if (assetsBvProductSelections.length > 0) {
          const assetsBvResp = await fetchAssetsBvData(asof, weights, securitySelection, day1Expense, updatedSpreads, updatedYields, reinvestmentYield);
          setAllAssetsBvData(assetsBvResp?.data?.data || assetsBvResp?.data || {});
        } else {
          setAllAssetsBvData({});
        }
        
        setLoadingGraph(false);
      } else {
        // No product selected, clear charts
        setAllAlmData({});
        setAllAssetsBvData({});
      }
    } catch (error) {
      if (!notifyPermissionError(showNotification, error)) {
        console.error("Error loading data:", error);
      }
    }

    setIsLoadingData(false);
  };

  // Function to clear data and set changes detected
  const clearDataAndSetChanges = () => {
    setMetricsData([]);
    setAllAlmData({});
    setAllAssetsBvData({});
    setChangesDetected(true);
  };

  // Clear data when dynamic columns change
  useEffect(() => {
    clearDataAndSetChanges();
  }, [dynamicColumns]);

  // Clear data when date changes (but don't update weights here to avoid infinite loops)
  useEffect(() => {
    if (assetData.length && asof) {
      setMetricsData([]);
      setAllAlmData({});
      setAllAssetsBvData({});
      setChangesDetected(true);
      // Clear spread and yield edits when date changes
      setUpdatedSpreads(null);
      setUpdatedYields(null);
      // Clear original asset data when date changes
      setOriginalAssetData([]);
    }
  }, [asof]);

  useEffect(() => {
    if (!asof) return;
    (async () => {
      try {
        const bulkResp = await fetchAllWeights(asof);
        setStaticWeights(bulkResp.data);
      } catch (error) {
        if (!notifyPermissionError(showNotification, error)) {
          console.error("Error fetching bulk weights:", error);
        }
      }
    })();
  }, [asof]);

  useEffect(() => {
    if (!asof) return;
    (async () => {
      try {
        const assetResp = await fetchAssetClassData(asof);
        const newAssetData = assetResp.data || [];
        console.log('📥 Fetched asset data:', { length: newAssetData.length, firstAsset: newAssetData[0] });
        setAssetData(newAssetData);
        // Store original asset data for comparison
        setOriginalAssetData([...newAssetData]);
        console.log('💾 Set original asset data:', { length: newAssetData.length });
      } catch (error) {
        if (!notifyPermissionError(showNotification, error)) {
          console.error("Error fetching asset data:", error);
        }
      }
    })();
  }, [asof]);

  // Update asset weights when staticWeights changes
  useEffect(() => {
    if (assetData.length && Object.keys(staticWeights).length > 0 && dynamicColumns.length > 0) {
      setAssetData((prevAssets) =>
        prevAssets.map((asset) => {
          const newWeights = { ...(asset.weights || {}) };
          
          // Update weights for each selected product
          dynamicColumns.forEach((col) => {
            const field = col.field;
            const weightsObj = staticWeights[field];
            if (weightsObj) {
              const assetName = asset.name || asset.cusip || "unknown";
              newWeights[field] = weightsObj[assetName] ?? 0;
            }
          });
          
          return { ...asset, weights: newWeights };
        })
      );
      
      // Also update originalAssetData with the same weights
      setOriginalAssetData((prevOriginalAssets) =>
        prevOriginalAssets.map((asset) => {
          const newWeights = { ...(asset.weights || {}) };
          
          // Update weights for each selected product
          dynamicColumns.forEach((col) => {
            const field = col.field;
            const weightsObj = staticWeights[field];
            if (weightsObj) {
              const assetName = asset.name || asset.cusip || "unknown";
              newWeights[field] = weightsObj[assetName] ?? 0;
            }
          });
          
          return { ...asset, weights: newWeights };
        })
      );
    }
  }, [staticWeights, assetData.length, dynamicColumns]);

  // Remove the initial load effect - we want manual loading only

  const handleAddProduct = async (region, tenor) => {
    const field = `${tenor}_${region}`;
    if (dynamicColumns.some((col) => col.field === field)) return;

    // Reset shock selections
    setMetricsShockSelection(['No Shock']);
    setAlmShockSelection('No Shock');
    setAssetsBvShockSelections(['No Shock']);

    const newCol = {
      field,
      headerName: `${region} ${tenor}`,
    };
    setDynamicColumns((prev) => [...prev, newCol]);

    const weightsObj = staticWeights[field];
    if (weightsObj) {
      setAssetData((prevAssets) =>
        prevAssets.map((asset) => {
          const newWeights = { ...(asset.weights || {}) };
          const assetName = asset.name || asset.cusip || "unknown";
          newWeights[field] = weightsObj[assetName] ?? 0;
          return { ...asset, weights: newWeights };
        })
      );
    }

    if (almProductSelection === '') {
      setAlmProductSelection(field);
    }
    if (!assetsBvProductSelections.includes(field)) {
      setAssetsBvProductSelections(prev => [...prev, field]);
    }

    // Clear data and set changes detected
    clearDataAndSetChanges();
  };

  const handleRemoveProduct = (field) => {
    // Reset shock selections
    setMetricsShockSelection(['No Shock']);
    setAlmShockSelection('No Shock');
    setAssetsBvShockSelections(['No Shock']);

    setDynamicColumns((prev) => prev.filter((col) => col.field !== field));
    setAssetData((prevAssets) =>
      prevAssets.map((asset) => {
        if (asset.weights && asset.weights[field] !== undefined) {
          const newWeights = { ...asset.weights };
          delete newWeights[field];
          return { ...asset, weights: newWeights };
        }
        return asset;
      })
    );

    if (almProductSelection === field) {
      setAlmProductSelection('');
    }
    if (assetsBvProductSelections.includes(field)) {
      setAssetsBvProductSelections(prev => prev.filter(selection => selection !== field));
    }

    // Clear data and set changes detected
    clearDataAndSetChanges();
  };

  const handleCellValueChange = (params) => {
    const { data, colDef, newValue } = params;
    setAssetData((prev) => {
      const newAssets = prev.map((asset) => {
        if (asset.cusip === data.id) {
          if (baseFields.includes(colDef.field)) {
            let value = newValue;
            if (
              ["zspread", "mkt_px", "eff_dur", "cvx", "mkt_yld", "wal"].includes(
                colDef.field
              )
            ) {
              value = parseFloat(newValue) || 0;
            } else if (colDef.field === "mkt_data_date") {
              value = dayjs(newValue, "M/D/YYYY").isValid()
                ? dayjs(newValue, "M/D/YYYY").format("YYYY-MM-DD")
                : newValue;
            }
            return { ...asset, [colDef.field]: value };
          } else {
            const parsedValue = (parseFloat(newValue) || 0) / 100;
            return {
              ...asset,
              weights: {
                ...asset.weights,
                [colDef.field]: parsedValue,
              },
            };
          }
        }
        return asset;
      });
      return newAssets;
    });

    // Handle spread and yield edits
    if (colDef.field === 'zspread' || colDef.field === 'mkt_yld') {
      const newValue = parseFloat(params.newValue) || 0;
      const originalValue = originalAssetData.find(asset => asset.cusip === data.cusip)?.[colDef.field];
      
      console.log('Cell edit detected:', {
        field: colDef.field,
        cusip: data.cusip,
        newValue,
        originalValue,
        difference: Math.abs(newValue - (originalValue || 0))
      });
      
      if (Math.abs(newValue - (originalValue || 0)) > 0.001) {
        // Value has changed, track the edit
        if (colDef.field === 'zspread') {
          const spreads = updatedSpreads || {};
          spreads[data.cusip] = newValue;
          console.log('Updating spreads:', spreads);
          setUpdatedSpreads(spreads);
        } else if (colDef.field === 'mkt_yld') {
          const yields = updatedYields || {};
          yields[data.cusip] = newValue;
          console.log('Updating yields:', yields);
          setUpdatedYields(yields);
        }
      } else {
        // Value is back to original, clear the edit
        if (colDef.field === 'zspread') {
          if (updatedSpreads) {
            const spreads = { ...updatedSpreads };
            delete spreads[data.cusip];
            console.log('Clearing spread edit for', data.cusip, 'remaining:', spreads);
            setUpdatedSpreads(Object.keys(spreads).length > 0 ? spreads : null);
          }
        } else if (colDef.field === 'mkt_yld') {
          if (updatedYields) {
            const yields = { ...updatedYields };
            delete yields[data.cusip];
            console.log('Clearing yield edit for', data.cusip, 'remaining:', yields);
            setUpdatedYields(Object.keys(yields).length > 0 ? yields : null);
          }
        }
      }
      
      // Force grid refresh to update cell renderer
      setTimeout(() => {
        if (gridRef.current && gridRef.current.api) {
          gridRef.current.api.refreshCells();
        }
      }, 100);
      
      // Clear data and set changes detected for spread/yield edits
      clearDataAndSetChanges();
    } else if (!baseFields.includes(colDef.field)) {
      // Handle weight edits
      const field = colDef.field;
      const newValue = parseFloat(params.newValue) || 0;
      const staticWeight = staticWeights[field]?.[data.name || data.cusip] || 0;
      const originalValue = staticWeight * 100;
      
      console.log('Weight edit detected:', {
        field,
        cusip: data.cusip,
        newValue,
        originalValue,
        difference: Math.abs(newValue - originalValue)
      });
      
      if (Math.abs(newValue - originalValue) > 0.001) {
        // Value has changed, track the edit
        const weights = { ...updatedWeights };
        if (!weights[field]) {
          weights[field] = {};
        }
        weights[field][data.cusip] = newValue / 100;
        console.log('Updating weights:', weights);
        setUpdatedWeights(weights);
      } else {
        // Value is back to original, clear the edit
        if (updatedWeights[field]) {
          const weights = { ...updatedWeights };
          delete weights[field][data.cusip];
          if (Object.keys(weights[field]).length === 0) {
            delete weights[field];
          }
          console.log('Clearing weight edit for', data.cusip, 'remaining:', weights);
          setUpdatedWeights(Object.keys(weights).length > 0 ? weights : {});
        }
      }
      
      // Clear data and set changes detected when weights change
      clearDataAndSetChanges();
    }
  };

  // Compose grid-config wiring with the grid's own callbacks so neither
  // clobbers the other. PamGrid wraps onGridReady internally (autosize) and
  // then invokes this; we forward to gridConfigs so a default layout can
  // auto-apply once the API is live. onFilterChanged keeps the local stash
  // in sync with filter gestures (column events ride through stashBindings).
  const handleGridReady = useCallback(
    (params) => {
      gridConfigs.onGridReady(params);
    },
    [gridConfigs]
  );

  const handleFilterChanged = useCallback(
    (params) => {
      gridConfigs.onGridStateChanged(params);
    },
    [gridConfigs]
  );

  // GridConfigMenu is stateful (dropdown + dialog) and needs gridRef, so it
  // is hosted in PamGrid's customToolbarComponents slot (a real, rendered
  // toolbar) rather than the {label, onClick} customToolbarButtons array.
  const customToolbarComponents = useMemo(
    () => [<GridConfigMenu key="grid-config-menu" controller={gridConfigs} />],
    [gridConfigs]
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: 4,
        bgcolor: "background.paper",
        p: 2,
        borderRadius: 2,
        minHeight: "100vh",
      }}
    >
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="ASOF"
                value={asof ? dayjs(asof) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    setAsof(formatted);
                    
                    // Clear everything when date changes
                    setMetricsData([]);
                    setAllAlmData({});
                    setAllAssetsBvData({});
                    setDynamicColumns([]);
                    setAlmProductSelection('');
                    setAssetsBvProductSelections([]);
                    setChangesDetected(true);
                  }
                }}
                shouldDisableDate={(date) =>
                  !availableDates.includes(dayjs(date).format("YYYY-MM-DD"))
                }
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
            <TextField
              label="Security Selection"
              type="number"
              value={securitySelectionInput}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setSecuritySelectionInput('');
                } else {
                  const numValue = parseInt(value);
                  if (!isNaN(numValue)) {
                    setSecuritySelectionInput(numValue);
                  }
                }
                clearDataAndSetChanges();
              }}
              onBlur={() => {
                if (securitySelectionInput === '' || isNaN(securitySelectionInput)) {
                  setSecuritySelectionInput(50);
                }
              }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="Additional Day 1 Premium"
              type="number"
              value={day1ExpenseInput}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setDay1ExpenseInput('');
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    setDay1ExpenseInput(numValue);
                  }
                }
                clearDataAndSetChanges();
              }}
              onBlur={() => {
                if (day1ExpenseInput === '' || isNaN(day1ExpenseInput)) {
                  setDay1ExpenseInput(0);
                }
              }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="Reinvestment Yield (%)"
              type="number"
              value={reinvestmentYieldInput}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setReinvestmentYieldInput('');
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    setReinvestmentYieldInput(numValue);
                  }
                }
                clearDataAndSetChanges();
              }}
              onBlur={() => {
                if (reinvestmentYieldInput === '' || isNaN(reinvestmentYieldInput)) {
                  setReinvestmentYieldInput(0);
                }
              }}
              sx={{ minWidth: 150 }}
            />
            <Autocomplete
              multiple
              options={cusipOptions}
              value={cusipFilter}
              onChange={(event, newValue) => {
                setCusipFilter(newValue);
                clearDataAndSetChanges();
              }}
              renderInput={(params) => (
                <TextField {...params} label="CUSIP Filter" />
              )}
              sx={{ minWidth: 200 }}
            />
            <ProductManager
              dynamicColumns={dynamicColumns}
              onAddProduct={handleAddProduct}
              onRemoveProduct={handleRemoveProduct}
            />
          </Box>
          
          {/* Load Data Button and Change Detection Alert */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            {changesDetected && (
              <Alert severity="info" sx={{ flexGrow: 1 }}>
                Changes detected. Click "Load Data" to refresh metrics and charts.
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={handleLoadData}
              disabled={isLoadingData || !changesDetected}
              sx={{ backgroundColor: 'lightgrey', color: 'black' }}
            >
              {isLoadingData ? 'Loading...' : 'Load Data'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Asset Data
          </Typography>
          
          {/* Reset buttons for edited columns */}
          {(updatedSpreads !== null || updatedYields !== null || Object.keys(updatedWeights).length > 0) && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                Reset edits:
              </Typography>
              {updatedSpreads !== null && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => handleResetField('zspread')}
                  sx={{ fontSize: '11px', minHeight: '24px' }}
                >
                  ZSpread ({Object.keys(updatedSpreads).length})
                </Button>
              )}
              {updatedYields !== null && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => handleResetField('mkt_yld')}
                  sx={{ fontSize: '11px', minHeight: '24px' }}
                >
                  Market Yield ({Object.keys(updatedYields).length})
                </Button>
              )}
              {Object.keys(updatedWeights).map((field) => (
                <Button
                  key={field}
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => handleResetField(field)}
                  sx={{ fontSize: '11px', minHeight: '24px' }}
                >
                  {dynamicColumns.find(col => col.field === field)?.headerName || field} ({Object.keys(updatedWeights[field]).length})
                </Button>
              ))}
            </Box>
          )}
          
          <div style={{ width: "100%" }}>
            <PamGrid
              ref={gridRef}
              modules={[
                ClientSideRowModelModule,
                TextFilterModule,
                NumberEditorModule,
                RenderApiModule,
                ValidationModule,
                PinnedRowModule,
                NumberFilterModule,
                DateFilterModule,
                CellStyleModule,
              ]}
              rowData={rowData}
              columnDefs={[...baseColumns, ...dynamicDefs]}
              defaultColDef={defaultColDef}
              getRowId={(data) => data.id}
              height={400}
              onCellValueChanged={handleCellValueChange}
              onGridReady={handleGridReady}
              onFilterChanged={handleFilterChanged}
              // Host the view-options menu in a rendered toolbar.
              showToolbar
              customToolbarComponents={customToolbarComponents}
              // Keep the toolbar minimal — this grid had no quick filter,
              // export button, clear-filters button, footer, or legend before.
              showQuickFilter={false}
              showExport={false}
              showClearFilters={false}
              showFooter={false}
              // Everything the raw grid passed that PamGrid has no named prop
              // for is routed through agGridProps verbatim so behavior is
              // preserved (delta/immutable row data, cell-change flash, the
              // pinned sum row), plus the stash bindings for column gestures.
              agGridProps={{
                immutableData: true,
                deltaRowDataMode: true,
                getRowNodeId: (params) => params.data.id,
                enableCellChangeFlash: true,
                pinnedBottomRowData: pinnedBottomRowData,
                ...gridConfigs.stashBindings,
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Info banner about shock availability */}
      <Typography variant="body2" color="info.dark" sx={{ mb: 4 }}>
        ℹ️ Note: Shock scenarios are only available for SPDA on 5/28/2025 or FIA NY on or after 7/16/2025
      </Typography>

      <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
        {/* Left Column: Metrics Panel */}
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            {loadingMetrics ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 300,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {metricsData && Object.keys(metricsData).length > 0 ? (
                      <MetricsPanel 
                        metrics={{ data: metricsData }} 
                        shockSelection={metricsShockSelection}
                        onShockSelectionChange={(newSelection) => {
                          setMetricsShockSelection(newSelection);
                        }}
                        region={currentRegions.length > 0 ? currentRegions[0] : ''}
                        asof={asof}
                        securitySelection={securitySelection}
                        assetData={assetData}
                        dynamicColumns={dynamicColumns}
                        reinvestmentYield={reinvestmentYield}
                      />
                    ) : (
                  <Typography variant="body1" color="text.secondary">
                    No metrics data available.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Grid>
                {/* Right Column: Charts */}
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            {loadingGraph ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 300,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {(almData && (almData.data || almData.data_up || almData.data_down)) || (assetsBvData && assetsBvData.length > 0) ? (
                  <Card variant="outlined" sx={{ width: "100%", height: "100%" }}>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                        <Typography variant="h6">Graphs</Typography>
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={scaleEnabled}
                                onChange={(e) => {
                                  setScaleEnabled(e.target.checked);
                                }}
                                color="primary"
                              />
                            }
                            label="Scale"
                          />
                        </Box>
                      </Box>

                      {almData && (almData.data || almData.data_up || almData.data_down) ? (
                        <Box sx={{ mb: 4 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">ALM</Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                              <FormControl sx={{ minWidth: 180 }}>
                                <InputLabel id="alm-product-label">ALM Product</InputLabel>
                                <Select
                                  labelId="alm-product-label"
                                  value={almProductSelection}
                                  label="ALM Product"
                                  onChange={(e) => {
                                    setAlmProductSelection(e.target.value);
                                  }}
                                >
                                  {dynamicColumns.map((col) => (
                                    <MenuItem key={col.field} value={col.field}>
                                      {col.headerName}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl sx={{ minWidth: 180 }}>
                                <InputLabel id="alm-shock-label">Shock Scenario</InputLabel>
                                <Select
                                  labelId="alm-shock-label"
                                  value={almShockSelection}
                                  label="Shock Scenario"
                                  onChange={(e) => {
                                    setAlmShockSelection(e.target.value);
                                  }}
                                >
                                  <MenuItem value="No Shock">No Shock</MenuItem>
                                  <MenuItem 
                                    value="Up 100 shock" 
                                    disabled={!isShockEnabled}
                                  >
                                    Up 100 shock
                                  </MenuItem>
                                  <MenuItem 
                                    value="Down 100 shock" 
                                    disabled={!isShockEnabled}
                                  >
                                    Down 100 shock
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>
                          <AlmChart 
                            data={almData} 
                            shockSelection={almShockSelection}
                            scaleEnabled={scaleEnabled}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No ALM data available.
                        </Typography>
                      )}
                      {assetsBvData && assetsBvData.length > 0 ? (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Assets vs Liabilities</Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                              <FormControl sx={{ minWidth: 180 }}>
                                <InputLabel id="assetsbv-product-label">AvL Products</InputLabel>
                                <Select
                                  labelId="assetsbv-product-label"
                                  multiple
                                  value={assetsBvProductSelections}
                                  label="AvL Products"
                                  onChange={(e) => {
                                    setAssetsBvProductSelections(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
                                  }}
                                  renderValue={(selected) => selected.map(sel => {
                                    const col = dynamicColumns.find(c => c.field === sel);
                                    return col ? col.headerName : sel;
                                  }).join(', ')}
                                >
                                  {dynamicColumns.map((col) => (
                                    <MenuItem key={col.field} value={col.field}>
                                      <Checkbox checked={assetsBvProductSelections.indexOf(col.field) > -1} />
                                      <ListItemText primary={col.headerName} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl sx={{ minWidth: 180 }}>
                                <InputLabel id="assetsbv-shock-label">Shock Scenarios</InputLabel>
                                <Select
                                  labelId="assetsbv-shock-label"
                                  multiple
                                  value={assetsBvShockSelections}
                                  label="Shock Scenarios"
                                  onChange={(e) => {
                                    setAssetsBvShockSelections(e.target.value);
                                  }}
                                  renderValue={(selected) => selected.join(', ')}
                                >
                                  <MenuItem value="No Shock">
                                    <Checkbox checked={assetsBvShockSelections.indexOf('No Shock') > -1} />
                                    <ListItemText primary="No Shock" />
                                  </MenuItem>
                                  <MenuItem 
                                    value="Up 100 shock" 
                                    disabled={!isShockEnabled}
                                  >
                                    <Checkbox checked={assetsBvShockSelections.indexOf('Up 100 shock') > -1} />
                                    <ListItemText primary="Up 100 shock" />
                                  </MenuItem>
                                  <MenuItem 
                                    value="Down 100 shock" 
                                    disabled={!isShockEnabled}
                                  >
                                    <Checkbox checked={assetsBvShockSelections.indexOf('Down 100 shock') > -1} />
                                    <ListItemText primary="Down 100 shock" />
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>
                          <AssetsBvChart 
                            datasets={assetsBvData} 
                            shockSelections={assetsBvShockSelections}
                            scaleEnabled={scaleEnabled}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No Assets vs Liabilities data available.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No graph data available.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
